import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import type { ChatbotState } from '~/redux/slices/chatbot/types';
import type { Entity, EntityType } from '~/types/Entity';
import type { Nullable } from '~/types/Nullable';

import { slices } from '~/redux/slices';

import type { EntityContext, EntityContextItem } from '../types/chatbot';

export interface OpenAskAiAndSendOptions {
  context?: EntityContext;
  selectedContext?: Nullable<EntityContextItem>;
  entity?: Nullable<Entity<EntityType>>;
  width?: ChatbotState['width'];
  panelMode?: ChatbotState['mode'];
}

export interface QueuedMessage {
  id: string;
  text: string;
}

export interface AskAiContextValue {
  queuedMessage: Nullable<QueuedMessage>;
  openAskAiAndSend: (
    message: string,
    options?: OpenAskAiAndSendOptions,
  ) => void;
  clearQueuedMessage: (id?: string) => void;
}

const AskAiContext = createContext<AskAiContextValue | undefined>(undefined);

export const AskAiProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const [queuedMessage, setQueuedMessage] =
    useState<Nullable<QueuedMessage>>(null);

  const clearQueuedMessage = useCallback((id?: string) => {
    setQueuedMessage((current) => {
      if (!current) {
        return null;
      }
      if (id !== undefined && current.id !== id) {
        return current;
      }
      return null;
    });
  }, []);

  const isVisible = useSelector(
    slices.chatbot.selectors.selectIsChatbotVisible,
  );
  const wasVisibleRef = useRef(isVisible);
  useEffect(() => {
    if (wasVisibleRef.current && !isVisible) {
      clearQueuedMessage();
    }
    wasVisibleRef.current = isVisible;
  }, [isVisible, clearQueuedMessage]);

  const openAskAiAndSend = useCallback(
    (message: string, options: OpenAskAiAndSendOptions = {}) => {
      const { context, selectedContext, entity, width, panelMode } = options;

      if (context) {
        dispatch(slices.chatbot.actions.setContext(context));
      }
      if (entity !== undefined) {
        dispatch(slices.chatbot.actions.overrideEntity(entity));
      }
      if (width !== undefined) {
        dispatch(slices.chatbot.actions.setWidth(width));
      }
      // Only apply the requested mode when this call is actually opening the
      // panel — otherwise a second send while it's already open (e.g. after
      // the person manually condensed it) would silently re-expand it.
      if (panelMode !== undefined && !isVisible) {
        dispatch(slices.chatbot.actions.setPanelMode(panelMode));
      }

      setQueuedMessage({ id: uuid(), text: message });
      dispatch(slices.chatbot.actions.openChatbot());
      if (selectedContext !== undefined) {
        dispatch(slices.chatbot.actions.setSelectedContext(selectedContext));
      }
    },
    [dispatch, isVisible],
  );

  const value = useMemo(
    () => ({ queuedMessage, openAskAiAndSend, clearQueuedMessage }),
    [queuedMessage, openAskAiAndSend, clearQueuedMessage],
  );

  return (
    <AskAiContext.Provider value={value}>{children}</AskAiContext.Provider>
  );
};

export const useAskAi = (): AskAiContextValue | undefined =>
  useContext(AskAiContext);
